import { applyReportBookingWithNotices, ReportBookingOpts } from './reportBooking';
import { planDueDateAfterSend } from './dateService';
// NUR der Typ: dieses Modul bleibt bewusst frei vom Supabase-Client (siehe
// ResumeDeps). Die Abbildung selbst wird erst beim Nachholen geladen.
import type { JournalMirrorRequest } from './serviceJournal';

/**
 * Crash-safe persistence for "report was emailed": the email leaves the house
 * first, then booking + archive snapshot + send log are written. If the app is
 * killed in between (mobile browsers freeze background tabs immediately), the
 * marker stored here is replayed on the next app start — otherwise the email
 * exists but billing/archive/send log are silently missing.
 *
 * The marker is written right AFTER the send succeeded (never before, so an
 * unsent email can never be booked) and removed once everything is persisted.
 */
export interface PendingSendEmailLog {
  project_id: string;
  project_name: string;
  recipients: string;
  subject: string;
  status: 'success' | 'error';
  body_html?: string;
  pdf_url?: string;
  error_message?: string;
  /**
   * Der Idempotenz-Schlüssel des Sendevorgangs (siehe newSendKey). Er MUSS mit
   * im Marker liegen: der Replay unten läuft nach einem Neustart oder in einem
   * zweiten Tab, also außerhalb jeder Laufzeitvariable des Auslösers. Nur weil
   * der Schlüssel den Marker überlebt, erkennt die Datenbank die nachgeholte
   * Zeile als dieselbe wie eine eventuell schon geschriebene.
   *
   * Optional, damit Marker aus einer älteren Version (ohne Schlüssel) weiter
   * abgespielt werden können, statt beim Parsen zu scheitern.
   */
  send_key?: string;
}

/**
 * Ein Schlüssel je Sendevorgang — erzeugt IMMER dort, wo der Nutzer den Vorgang
 * auslöst, nie beim Schreiben. Ein erst im Insert erzeugter Schlüssel wäre bei
 * jedem Versuch ein anderer und der Unique-Index liefe ins Leere; genau das ist
 * der Grund, warum Bericht #13 in Projekt 26041014 zweimal im Sendeverlauf
 * stand (zwei Wege, 52 ms auseinander, ohne zweiten Klick).
 *
 * Ein bewusster Zweitversand ist ein eigener Vorgang und holt sich hier einen
 * eigenen Schlüssel — die Wiederholung im Sendeverlauf bleibt dadurch möglich.
 */
export const newSendKey = (): string => crypto.randomUUID();

export interface PendingSendMarker {
  id: string;
  projectId: string;
  createdAt: string;
  attempts: number;
  booking: ReportBookingOpts;
  /** null once the send log row was written — prevents duplicate log entries on retry. */
  emailLog: PendingSendEmailLog | null;
  /**
   * Wann ein Durchgang diesen Marker in Arbeit genommen hat — die Reservierung.
   *
   * Sie ist der zweite von zwei Riegeln gegen die MEHRFACHBUCHUNG desselben
   * Berichts. Der erste (`laufenderDurchgang`) haelt einen zweiten Durchgang im
   * selben Tab auf; dieser hier wirkt ueber Tabs und Neustarts hinweg, weil er
   * im Storage steht.
   *
   * Warum es beide braucht: `attempts` wird zwar vor der Arbeit erhoeht, sagt
   * aber nur, WIE OFT versucht wurde, nicht ob gerade jemand dran ist — bis
   * MAX_RESUME_ATTEMPTS nimmt jeder Durchgang den Marker trotzdem. Und der
   * reportRef-Guard in `applyReportBookingWithNotices` prueft gegen den
   * GELADENEN Stand: laufen zwei Durchgaenge gleichzeitig, hat keiner die
   * Buchung des anderen gesehen, und beide buchen.
   *
   * Genau das ist in Projekt 6110 passiert (02.09.2026): Bericht #25 ging um
   * 07:07 raus, die Buchung lief 52 Minuten spaeter DREIMAL innerhalb von 546
   * Millisekunden. Im Archiv liegt ein Bericht #25, im Protokoll standen drei
   * Begehungen — 330 EUR, die niemandem zustehen. In 6185 dasselbe mit zwei
   * Zeilen, 58 ms auseinander.
   *
   * Optional, damit Marker aus einer aelteren Version weiter abgespielt werden.
   */
  startedAt?: string | null;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const STORAGE_KEY = 'osia_pending_send_v1';
export const MAX_RESUME_ATTEMPTS = 5;

const memoryFallback = new Map<string, string>();
const defaultStorage = (): StorageLike => {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch { /* z. B. blockierte Cookies/Storage */ }
  return {
    getItem: (k: string) => memoryFallback.get(k) ?? null,
    setItem: (k: string, v: string) => { memoryFallback.set(k, v); },
    removeItem: (k: string) => { memoryFallback.delete(k); },
  };
};

export const readPendingSendMarkers = (storage: StorageLike = defaultStorage()): PendingSendMarker[] => {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeMarkers = (markers: PendingSendMarker[], storage: StorageLike) => {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(markers));
  } catch (e) {
    console.warn('Pending-Send-Marker konnte nicht gespeichert werden', e);
  }
};

export const addPendingSendMarker = (
  marker: Omit<PendingSendMarker, 'id' | 'createdAt' | 'attempts'>,
  storage: StorageLike = defaultStorage(),
): PendingSendMarker | null => {
  const full: PendingSendMarker = {
    ...marker,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  writeMarkers([...readPendingSendMarkers(storage), full], storage);
  // Zurücklesen statt blind vertrauen: bei vollem/gesperrtem Storage gäbe es
  // sonst scheinbare Absicherung ohne echten Wiederherstellungspunkt — der
  // Aufrufer muss dann sofort selbst persistieren statt sich auf den Marker
  // zu verlassen.
  const persisted = readPendingSendMarkers(storage).some(m => m.id === full.id);
  return persisted ? full : null;
};

export const updatePendingSendMarker = (
  id: string,
  patch: Partial<PendingSendMarker>,
  storage: StorageLike = defaultStorage(),
) => {
  writeMarkers(readPendingSendMarkers(storage).map(m => (m.id === id ? { ...m, ...patch } : m)), storage);
};

export const removePendingSendMarker = (id: string, storage: StorageLike = defaultStorage()) => {
  writeMarkers(readPendingSendMarkers(storage).filter(m => m.id !== id), storage);
};

/**
 * Cloud functions injected instead of imported: keeps this module free of the
 * supabase client (unit-testable without env vars) and avoids a dependency
 * cycle with cloudService.
 */
export interface ResumeDeps {
  loadProjectById: (id: string) => Promise<any>;
  saveProjectToCloud: (report: any, projectId?: string) => Promise<any>;
  updateProjectExportStatus: (projectId: string, exportedNumber: number) => Promise<any>;
  saveReportSnapshot: (report: any, explicitProjectId?: string, actor?: any, sender?: any) => Promise<any>;
  logEmailAttempt: (log: PendingSendEmailLog) => Promise<any>;
  /**
   * Die Zweitschrift ins Buchungsjournal (S4b), in zwei Schritten: VOR dem
   * Speichern vormerken (Ausgangskorb), NACH dem bestaetigten Speichern
   * schreiben. Optional und mit spaeter Auflösung: der Aufrufer beim
   * App-Start muss nichts zusaetzlich mitgeben, und Tests koennen beide
   * Schritte beobachten, ohne den Supabase-Client zu laden.
   */
  queueJournalMirror?: (req: JournalMirrorRequest) => void;
  flushJournalMirror?: (projectId: string, savedLog?: unknown[] | null) => Promise<unknown>;
}

/**
 * Vormerken — VOR dem ersten Schreiben. Schluckt jeden Fehler.
 *
 * Warum vorher: am 07.09.2026 warf saveProjectToCloud bei acht Versendungen
 * zwischen Protokoll und Positionen. Die Zweitschrift stand erst danach im
 * Code und wurde nie aufgerufen — der Korb wusste nichts, die Zeilen fehlten
 * bis zum Nachtlauf. Liegt die Anfrage vorher im Korb, prueft
 * `retryJournalMirrors` nach einem Abbruch gegen den Server: traegt das JSON
 * die Zeile, wird sie angehaengt, sonst verworfen.
 */
const vormerken = async (
  req: JournalMirrorRequest,
  eigene?: ResumeDeps['queueJournalMirror'],
): Promise<void> => {
  try {
    const merken = eigene ?? (await import('./serviceJournal')).queueJournalMirror;
    merken(req);
  } catch (e) {
    console.warn('Zweitschrift ins Buchungsjournal nicht vormerkbar:', e);
  }
};

/**
 * Schreiben — NACH dem bestaetigten Speichern, gefiltert auf das Protokoll,
 * das wirklich gespeichert wurde. Schluckt jeden Fehler.
 *
 * Sie darf den Ausgang des Nachholens NIE aendern: eine ausgebliebene
 * Zweitschrift ist kein gescheitertes Nachholen, und ein hier durchgereichter
 * Fehler liesse den Marker liegen und den naechsten Start denselben Vorgang
 * erneut abspielen. Was hier scheitert, bleibt im Korb und wird nachgeholt.
 */
const schreiben = async (
  projectId: string,
  savedLog: unknown[] | null,
  eigene?: ResumeDeps['flushJournalMirror'],
): Promise<void> => {
  try {
    const flush = eigene ?? (await import('./serviceJournal')).flushJournalMirror;
    await flush(projectId, savedLog as never);
  } catch (e) {
    console.warn('Zweitschrift ins Buchungsjournal nicht moeglich:', e);
  }
};

/**
 * Wie lange die Reservierung eines Markers gilt (siehe `startedAt`).
 *
 * Lang genug, dass ein normaler Durchgang (laden, buchen, speichern,
 * archivieren) sicher darunter bleibt; kurz genug, dass ein abgestuerzter
 * Durchgang den Marker nicht dauerhaft blockiert — der naechste App-Start
 * liegt praktisch immer spaeter als zwei Minuten.
 */
export const RESERVIERUNG_MS = 2 * 60 * 1000;

/**
 * Der laufende Durchgang, solange einer laeuft.
 *
 * Der Ausloeser in App.tsx haengt an der Sitzung, und die wechselt bei jedem
 * Token-Refresh die Objektidentitaet — der Effekt startete das Nachholen
 * dadurch erneut, waehrend der vorige Durchgang noch lief. Ein zweiter Aufruf
 * bekommt jetzt DASSELBE Versprechen zurueck, statt einen zweiten Durchgang
 * zu eroeffnen.
 */
let laufenderDurchgang: Promise<number> | null = null;

/** Nur fuer Tests: den Riegel zwischen zwei Faellen loesen. */
export const resetResumeGuard = (): void => {
  laufenderDurchgang = null;
};

/** Replays all pending markers; returns how many were completed. */
export const resumePendingSendPersistence = (
  deps: ResumeDeps,
  storage: StorageLike = defaultStorage(),
): Promise<number> => {
  if (laufenderDurchgang) return laufenderDurchgang;
  laufenderDurchgang = durchgang(deps, storage).finally(() => {
    laufenderDurchgang = null;
  });
  return laufenderDurchgang;
};

const durchgang = async (
  deps: ResumeDeps,
  storage: StorageLike = defaultStorage(),
): Promise<number> => {
  const markers = readPendingSendMarkers(storage);
  let resumed = 0;

  for (const stale of markers) {
    // Frisch lesen: ein anderer Tab kann den Marker inzwischen abgearbeitet
    // haben (verkleinert das Doppelverarbeitungs-Fenster, eliminiert es nicht —
    // echte Idempotenz liefert der reportRef-Guard in applyReportBooking).
    const marker = readPendingSendMarkers(storage).find(m => m.id === stale.id);
    if (!marker) continue;

    // In Arbeit bei jemand anderem — Finger weg. Begruendung und der Vorfall,
    // aus dem dieser Riegel stammt, stehen an `startedAt`.
    const reserviertSeit = marker.startedAt ? Date.parse(marker.startedAt) : NaN;
    if (Number.isFinite(reserviertSeit) && Date.now() - reserviertSeit < RESERVIERUNG_MS) continue;

    if (marker.attempts >= MAX_RESUME_ATTEMPTS) {
      // Dead-Letter: keine Requests mehr, aber den einzigen Wiederherstellungs-
      // beleg nicht löschen. Erst nach 30 Tagen aufräumen.
      const ageMs = Date.now() - new Date(marker.createdAt).getTime();
      if (!Number.isFinite(ageMs) || ageMs > 30 * 24 * 60 * 60 * 1000) {
        console.error('Pending-Send-Marker nach 30 Tagen verworfen:', marker);
        removePendingSendMarker(marker.id, storage);
      } else {
        console.error('Pending-Send-Marker ausgesetzt (zu viele Versuche) — bleibt als Beleg erhalten:', marker);
      }
      continue;
    }
    // Reservieren UND zaehlen im selben Schreibvorgang, VOR jeder Arbeit: ein
    // zweiter Durchgang, der jetzt liest, sieht den Marker als vergeben.
    updatePendingSendMarker(
      marker.id,
      { attempts: marker.attempts + 1, startedAt: new Date().toISOString() },
      storage,
    );

    try {
      if (marker.emailLog) {
        await deps.logEmailAttempt(marker.emailLog);
        updatePendingSendMarker(marker.id, { emailLog: null }, storage);
      }

      const project = await deps.loadProjectById(marker.projectId);
      if (!project?.data) throw new Error(`Projekt ${marker.projectId} nicht ladbar`);

      // Export-Status, Archiv-Snapshot und Fälligkeitsplan nur, wenn der
      // Bericht noch derselbe ist wie beim Versand: Ist er inzwischen
      // fortgeschrieben (neue Begehung), würde der Snapshot den FALSCHEN
      // (aktuellen) Stand unter falscher Nummer archivieren. Die Buchung ist
      // davon unabhängig korrekt (reportRef).
      const meta = project.data?.metadata;
      const sameReport = meta?.inspectionNumber === marker.booking.inspectionNumber
        && (meta?.sectionId || null) === (marker.booking.sectionId || null);

      // Der Protokollstand VOR dem Nachholen — er liefert dem Journal die
      // bereits vergebenen Eintrags-ids.
      const previousLog = project.data?.metadata?.globalData?.serviceLog || [];
      const {
        report: gebuchtRoh,
        entries: neueEintraege,
        statements,
      } = applyReportBookingWithNotices(project.data, marker.booking);

      // `applyReportBookingWithNotices` setzt `metadata.isExported = true` —
      // richtig nur, solange der geladene Bericht noch DER versendete ist. Ist
      // er fortgeschrieben, traegt `project.data` die LAUFENDE, noch nicht
      // versendete Begehung; das Flag schaltete bei deren Versand die
      // Abrechnung ab (EmailModal `billingEnabled`). Dieselbe Rueckstellung
      // wie beim Nachbuchen (reportRebookService). Vorfall 6051: Marker #149
      // auf Rolfs Geraet, das Projekt stand beim Nachholen schon auf #150.
      const bookedReport = sameReport
        ? gebuchtRoh
        : { ...gebuchtRoh, metadata: { ...gebuchtRoh.metadata, isExported: meta?.isExported } };

      // Wie im Versand-Dialog: der Termin wandert über den JSON-Zweig mit,
      // den saveProjectToCloud in die Spalte schreibt. planDueDateAfterSend
      // ist monoton, sameReport ist deshalb ein zweiter Riegel und kein
      // notwendiger — ein Replay kann den Plan so oder so nicht zurückdrehen.
      const dueDate = sameReport
        ? planDueDateAfterSend(
            bookedReport.metadata?.date,
            bookedReport.metadata?.intervalConfig?.nextScheduledDate,
            bookedReport.metadata?.intervalConfig,
          )
        : null;
      const updatedReport = dueDate
        ? {
            ...bookedReport,
            metadata: {
              ...bookedReport.metadata,
              intervalConfig: { ...bookedReport.metadata.intervalConfig!, nextScheduledDate: dueDate },
            },
          }
        : bookedReport;

      // ── Zweitschrift ins Buchungsjournal (S4b): vormerken VOR dem Schreiben ──
      //
      // Nur mit den Zeilen, die dieses Nachholen erzeugt hat. Beim zweiten
      // Durchlauf desselben Markers greift der reportRef-Guard in
      // applyReportBooking, `neueEintraege` bleibt leer und es wird nichts
      // vorgemerkt — die Vormerkung des ERSTEN Durchlaufs liegt dann noch im
      // Korb und wird von `retryJournalMirrors` gegen den Server geprueft.
      //
      // `statements` kommt aus der Buchung selbst: nur dort steht fest, welche
      // Zeile die BEGEHUNG deckt und welche eine Zusatzleistung ist.
      //
      // Gebucht wird immer im Projekt des Markers; den Weg Tochter → Mutter
      // geht dieser Pfad nicht (siehe SiGeKoInspectionModule), deshalb
      // 'report_send'.
      if (neueEintraege.length > 0) {
        await vormerken(
          {
            projectId: marker.projectId,
            entries: neueEintraege,
            previousLog,
            source: 'report_send',
            linkedProjectConfig: project.data?.metadata?.globalData?.linkedProjectConfig ?? null,
            statements,
          },
          deps.queueJournalMirror,
        );
      }

      await deps.saveProjectToCloud(updatedReport, marker.projectId);

      // Erst NACH dem bestaetigten Speichern schreiben, gefiltert auf das
      // gespeicherte Protokoll.
      if (neueEintraege.length > 0) {
        await schreiben(
          marker.projectId,
          updatedReport.metadata?.globalData?.serviceLog ?? null,
          deps.flushJournalMirror,
        );
      }

      if (sameReport) {
        await deps.updateProjectExportStatus(marker.projectId, marker.booking.inspectionNumber);
        // Der URSPRÜNGLICHE Versender gehört ins Archiv, nicht wer gerade
        // eingeloggt ist: Der Marker kann auf einem geteilten Gerät nach einem
        // Kontowechsel nachgeholt werden, und der conductor_name wird
        // serverseitig nur beim Anlegen gesetzt — beim Replay ist das genau der
        // Insert. marker.booking.performerName ist beim Versand festgehalten
        // worden (EmailModal.bookingOpts).
        // Auch als VERSENDER der beim Versand festgehaltene Name, nicht der
        // gerade eingeloggte Nutzer: der Replay laeuft womoeglich unter einem
        // anderen Konto (Baustellen-Tablet).
        await deps.saveReportSnapshot(
          updatedReport, marker.projectId, marker.booking.performerName, marker.booking.performerName);
      } else {
        console.warn('Pending-Send-Replay: Bericht wurde fortgeschrieben — Export-Status/Snapshot übersprungen, Buchung nachgeholt.');
      }

      removePendingSendMarker(marker.id, storage);
      resumed++;
    } catch (e) {
      console.error('Nachholen von Buchung/Archiv fehlgeschlagen — Marker bleibt erhalten:', e);
      // Reservierung sofort zurueckgeben: der Durchgang ist fertig, nur eben
      // erfolglos. Sie liegen zu lassen liesse den naechsten Versuch zwei
      // Minuten warten, ohne dass jemand am Marker arbeitet — der Riegel soll
      // GLEICHZEITIGE Durchgaenge trennen, nicht Wiederholungen bremsen.
      // Stirbt der Durchgang vorher (Tab geschlossen), laeuft die Reservierung
      // nach RESERVIERUNG_MS von selbst ab.
      updatePendingSendMarker(marker.id, { startedAt: null }, storage);
    }
  }

  return resumed;
};
